import type { OptionValue } from '@/components/organisms/Select/types';
import { compactFilterRecord } from '@/lib/filters/compactFilters';
import { isDateRange } from '@/lib/filters/dateRange';
import { isRecord } from '@/lib/guards';
import { isOptionValueArray } from '@/lib/selectOptions';

import { isHazardousFilter } from './guards';

import type {
  CustomFilterApiKey,
  CustomFilterApiKeySet,
  CustomFilters,
  CustomFilterValues,
  NewFilterGroupCounts,
  NewFilterKey,
  NewAppliedFilters,
  NewDefaultFilters,
  NewFilters,
  NewFiltersClearOptions,
  NewMultiFilterKey,
} from './types';

export { isDateRange } from '@/lib/filters/dateRange';

export const DEFAULT_FILTERS_DRAFT: NewFilters = {
  countries: [],
  disposers: [],
  eurals: [],
  hazardous: null,
  locations: [],
  materials: [],
  period: [],
  processes: [],
  processors: [],
  projects: [],
  radius: [],
};

const DEFAULT_FILTER_KEY_SET = new Set<keyof NewDefaultFilters>(
  Object.keys(DEFAULT_FILTERS_DRAFT) as Array<keyof NewDefaultFilters>
);

const EMPTY_CLEARABLE_FILTERS: Pick<
  NewFilters,
  | 'countries'
  | 'disposers'
  | 'eurals'
  | 'hazardous'
  | 'locations'
  | 'materials'
  | 'processes'
  | 'processors'
  | 'projects'
  | 'radius'
> = {
  countries: [],
  disposers: [],
  eurals: [],
  hazardous: null,
  locations: [],
  materials: [],
  processes: [],
  processors: [],
  projects: [],
  radius: [],
};

function sanitizeHazardousFilter(value: unknown, fallback: NewFilters['hazardous']): NewFilters['hazardous'] {
  if (isHazardousFilter(value) || value === null) return value;
  if (typeof value === 'boolean') return value ? '1' : '0';

  return fallback;
}

function toStringOptions(values: OptionValue[]): string[] {
  return values.map(String);
}

function toNumberOptions(values: OptionValue[]): number[] {
  return values.map(Number).filter(Number.isFinite);
}

function toProcessOptions(values: OptionValue[]): OptionValue[] {
  return values.map((value) => {
    if (typeof value === 'number') return value;

    const numericValue = Number(value);

    return Number.isInteger(numericValue) ? numericValue : value;
  });
}

export function isDefaultFilterKey(key: string): key is keyof NewDefaultFilters {
  return DEFAULT_FILTER_KEY_SET.has(key as keyof NewDefaultFilters);
}

export function getCustomFilterApiKeys(customFilters: CustomFilters): CustomFilterApiKeySet {
  return new Set(Object.values(customFilters).map((filter) => filter.apiKey));
}

export function isAllowedCustomFilterApiKey(
  key: string,
  allowedKeys: CustomFilterApiKeySet
): key is CustomFilterApiKey {
  return allowedKeys.has(key as CustomFilterApiKey);
}

export function isPotentialCustomFilterApiKey(key: string): key is CustomFilterApiKey {
  return key.includes('_') && !isDefaultFilterKey(key);
}

export function getCustomFilterEntries(
  filters: Partial<NewFilters>,
  allowedKeys?: CustomFilterApiKeySet
): Array<[CustomFilterApiKey, OptionValue[]]> {
  return Object.entries(filters).filter(
    (entry): entry is [CustomFilterApiKey, OptionValue[]] =>
      (allowedKeys ? isAllowedCustomFilterApiKey(entry[0], allowedKeys) : isPotentialCustomFilterApiKey(entry[0])) &&
      isOptionValueArray(entry[1])
  );
}

function cloneDefaultFilters(filters: NewDefaultFilters): NewDefaultFilters {
  return {
    countries: [...filters.countries],
    disposers: [...filters.disposers],
    eurals: [...filters.eurals],
    hazardous: filters.hazardous,
    locations: [...filters.locations],
    materials: [...filters.materials],
    period: [...filters.period],
    processes: [...filters.processes],
    processors: [...filters.processors],
    projects: [...filters.projects],
    radius: [...filters.radius],
  };
}

export function cloneFiltersDraft(filters: NewFilters): NewFilters {
  const customFilters = Object.fromEntries(
    getCustomFilterEntries(filters).map(([key, values]) => [key, [...values]])
  ) as CustomFilterValues;

  return {
    ...cloneDefaultFilters(filters),
    ...customFilters,
  };
}

export function createEmptyDraft(): NewFilters {
  return cloneFiltersDraft(DEFAULT_FILTERS_DRAFT);
}

export function compactFiltersDraft(
  filters: Partial<NewFilters>,
  allowedCustomFilterKeys?: CustomFilterApiKeySet
): NewAppliedFilters {
  if (!allowedCustomFilterKeys) return compactFilterRecord(filters);

  const customFilters = Object.fromEntries(getCustomFilterEntries(filters, allowedCustomFilterKeys));
  const defaultFilters = Object.fromEntries(
    Object.entries(filters).filter(([key]) => isDefaultFilterKey(key))
  ) as Partial<NewFilters>;

  return compactFilterRecord({ ...defaultFilters, ...customFilters });
}

/**
 * Clears user-editable filters while preserving option-derived requirements,
 * such as a single forced country.
 */
export function createClearedDraft(
  base: NewFilters = DEFAULT_FILTERS_DRAFT,
  options: NewFiltersClearOptions = {}
): NewFilters {
  return cloneFiltersDraft({
    ...DEFAULT_FILTERS_DRAFT,
    ...EMPTY_CLEARABLE_FILTERS,
    countries: options.preservedCountries ?? EMPTY_CLEARABLE_FILTERS.countries,
    period: [...base.period],
  });
}

export function clearFilter(filters: NewFilters, filter: NewFilterKey): NewFilters {
  if (filter === 'hazardous') return cloneFiltersDraft({ ...filters, hazardous: null });

  return cloneFiltersDraft({ ...filters, [filter]: [] });
}

export function removeFilterValue(filters: NewFilters, filter: NewMultiFilterKey, value: OptionValue): NewFilters {
  const values = filters[filter];

  return cloneFiltersDraft({
    ...filters,
    [filter]: Array.isArray(values) ? values.filter((item) => item !== value) : [],
  });
}

function countCustomFilterValues(filters: NewFilters): number {
  return getCustomFilterEntries(filters).reduce((total, [, value]) => total + value.length, 0);
}

export function countFilterGroups(filters: NewFilters): NewFilterGroupCounts {
  return {
    businessUnit: filters?.countries.length + filters?.disposers.length,
    origin: filters?.projects.length + filters?.locations.length,
    processing: filters?.processors.length + filters?.processes.length,
    waste: filters?.materials.length + filters?.eurals.length + (filters?.hazardous ? 1 : 0),
    general: filters?.radius.length,
    organization: countCustomFilterValues(filters),
  };
}

/**
 * Counts selected visible filters without option-derived readonly rules.
 * UI summaries that need to ignore a forced single country should use the
 * option-aware FiltersBar view helpers instead.
 */
export function countEditableFilters(filters: NewFilters): number {
  const counts = countFilterGroups(filters);

  return counts.businessUnit + counts.general + counts.organization + counts.origin + counts.processing + counts.waste;
}

/**
 * Converts unknown persisted data into a complete `NewFilters`.
 *
 * Invalid or missing fields fall back per-key, which lets the app tolerate
 * older localStorage payloads without discarding every valid saved filter.
 */
export function sanitizeFiltersDraft(value: unknown, fallback: NewFilters): NewFilters {
  if (!isRecord(value)) return cloneFiltersDraft(fallback);

  const customFilters = Object.fromEntries(getCustomFilterEntries(value as Partial<NewFilters>)) as CustomFilterValues;

  return {
    countries: isOptionValueArray(value.countries) ? toStringOptions(value.countries) : [...fallback.countries],
    disposers: isOptionValueArray(value.disposers) ? toStringOptions(value.disposers) : [...fallback.disposers],
    eurals: isOptionValueArray(value.eurals) ? toStringOptions(value.eurals) : [...fallback.eurals],
    hazardous: sanitizeHazardousFilter(value.hazardous, fallback.hazardous),
    locations: isOptionValueArray(value.locations) ? toStringOptions(value.locations) : [...fallback.locations],
    materials: isOptionValueArray(value.materials) ? toStringOptions(value.materials) : [...fallback.materials],
    period: isDateRange(value.period) ? value.period : [...fallback.period],
    processes: isOptionValueArray(value.processes) ? toProcessOptions(value.processes) : [...fallback.processes],
    processors: isOptionValueArray(value.processors) ? toStringOptions(value.processors) : [...fallback.processors],
    projects: isOptionValueArray(value.projects) ? toStringOptions(value.projects) : [...fallback.projects],
    radius: isOptionValueArray(value.radius) ? toNumberOptions(value.radius) : [...fallback.radius],
    ...customFilters,
  };
}
